import { useEffect, useState } from "react";
import { countState } from "../../actions/studensts";
import UNAUTHORIZED from "../UNAUTHORIZED";

// A single stat row inside a card
function Stat({ label, value, highlight }) {
  return (
    <div
      className={`flex items-center justify-between py-1.5 ${
        highlight ? "mt-1 border-t border-gray-100 pt-2" : ""
      }`}
    >
      <span
        className={`text-sm ${
          highlight ? "font-semibold text-gray-800" : "text-gray-500"
        }`}
      >
        {label}
      </span>
      <span
        className={`tabular-nums font-bold ${
          highlight ? "text-base text-gray-900" : "text-sm text-gray-700"
        }`}
      >
        {value ?? 0}
      </span>
    </div>
  );
}

// A card with a colored accent header
function Card({ title, accent, children }) {
  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 transition hover:-translate-y-0.5 hover:shadow-md">
      <div className={`h-1.5 w-full ${accent}`} />
      <div className="p-4">
        <h2 className="mb-2 text-base font-semibold text-gray-800">{title}</h2>
        <div className="divide-y divide-gray-50">{children}</div>
      </div>
    </div>
  );
}

export default function Overview() {
  const [size, setSize] = useState(null);
  const user = JSON.parse(localStorage.getItem("profile")).value[0].role;

  useEffect(() => {
    const fetchData = async () => {
      const res = await countState();
      setSize(res.data.size);
    };
    fetchData();
  }, []);

  if (user !== "admin") return <UNAUTHORIZED />;

  return (
    <div className="px-4 pb-10 sm:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Overview</h1>
          <p className="text-sm text-gray-300">
            Live snapshot of students across every stage
          </p>
        </div>
        <div>
          <div className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-white backdrop-blur">
            <span className="text-sm text-gray-200">Total Students</span>
            <span className="text-xl font-bold tabular-nums">
              {size?.total ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <Card title="Auditing" accent="bg-amber-500">
          <Stat label="New" value={size?.auditing} />
          <Stat label="Request Edit" value={size?.request_edit} />
          <Stat label="Total Auditing" value={size?.total_auditing} highlight />
        </Card>

        <Card title="Invoice" accent="bg-blue-500">
          <Stat label="New" value={size?.invoice} />
          <Stat label="Invoice Wait" value={size?.invoice_wait} />
          <Stat label="Another Invoice" value={size?.another_invoice} />
          <Stat label="Total Invoice" value={size?.total_invoice} highlight />
        </Card>

        <Card title="Score Request" accent="bg-indigo-500">
          <Stat label="Score Validation" value={size?.score_validation} />
          <Stat label="Score Request" value={size?.score_request} />
          <Stat label="Score Pending" value={size?.score_pending} />
          <Stat label="Total" value={size?.total_score_request} highlight />
        </Card>

        <Card title="Print" accent="bg-cyan-500">
          <Stat label="Print" value={size?.print} />
          <Stat label="Print Validation" value={size?.print_validation} />
          <Stat label="Total" value={size?.total_print} highlight />
        </Card>

        <Card title="MOE" accent="bg-emerald-500">
          <Stat label="MOE Validation" value={size?.moe_validation} />
          <Stat label="MOE Hold" value={size?.moe_hold} />
          <Stat label="Sending to MOE" value={size?.moe} />
          <Stat label="MOE Pending" value={size?.moe_pending} />
          <Stat label="AATC Review" value={size?.aatc_review} />
          <Stat label="Total" value={size?.total_moe} highlight />
        </Card>

        <Card title="Deliver" accent="bg-teal-500">
          <Stat label="Deliver Onsite" value={size?.deliver_onsite} />
          <Stat label="Aramex" value={size?.aramex} />
          <Stat label="Delivery Hold" value={size?.delivery_hold} />
          <Stat label="Total" value={size?.total_deliver} highlight />
        </Card>

        <Card title="MOHE" accent="bg-purple-500">
          <Stat label="MOHE Validation" value={size?.mohe_validation} />
          <Stat label="Sending to MOHE" value={size?.mohe} />
          <Stat label="Total" value={(size?.mohe_validation ?? 0) + (size?.mohe ?? 0)} highlight />
        </Card>

        <Card title="Finished Students" accent="bg-green-600">
          <Stat label="Done" value={size?.done} highlight />
        </Card>
      </div>
    </div>
  );
}
